import type { ICanvasContext, Page } from './types';
import { PageManager } from './page-manager';
import { CanvasRenderer } from './renderer';
import { InputManager } from './input-manager';
import { HistoryManager } from '../history';
import { type DrawingAction } from '../actions';
import { TextTool } from '../tools/text-tool';
import type { ToolConfig, DrawingTool, Point } from '../tools/types';
import { showToast } from '../ui/toast';
import { ToolManager } from './tool-manager';
import { SelectTool } from '../tools/select-tool';
import { PasteAction } from '../actions/paste-action';

export class CanvasManager implements ICanvasContext {
    public canvas: HTMLCanvasElement;
    public container: HTMLElement;
    public ctx: CanvasRenderingContext2D;
    public offscreenCanvas: HTMLCanvasElement;
    public offscreenCtx: CanvasRenderingContext2D;

    // State
    public scale: number = 1.0;
    public offset: Point = { x: 0, y: 0 };
    public currentTool: DrawingTool = 'pencil';
    public config: ToolConfig = { size: 5, color: '#000000', opacity: 100, hardness: 100, pressure: 50 };

    // Managers
    public pageManager: PageManager;
    public toolManager: ToolManager;
    public historyManager: HistoryManager;
    public textTool: TextTool | null = null;
    private renderer: CanvasRenderer;

    public onUpdateCallback: ((pageId?: string) => void) | null = null;
    public onZoomChange: ((zoomPercent: number) => void) | null = null;
    public postRenderCallback: (() => void) | null = null;

    constructor(canvasId: string, onUpdate?: () => void) {
        this.onUpdateCallback = onUpdate || null;
        this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('Failed to get 2d context from main canvas');
        this.ctx = ctx;

        if (!this.canvas.parentElement) throw new Error('Canvas must have a parent element');
        this.container = this.canvas.parentElement;

        this.offscreenCanvas = document.createElement('canvas');
        const offCtx = this.offscreenCanvas.getContext('2d');
        if (!offCtx) throw new Error('Failed to get 2d context from offscreen canvas');
        this.offscreenCtx = offCtx;

        this.renderer = new CanvasRenderer(this.ctx, this.canvas);
        this.pageManager = new PageManager(
            () => this.render(),
            (id) => this.onUpdateCallback?.(id)
        );
        this.historyManager = new HistoryManager();

        this.toolManager = new ToolManager(this);
        this.textTool = this.toolManager.getTool('text') as TextTool;

        new InputManager(this);

        // Initialize
        this.resize();
        this.addPage(1024, 1024);
        this.render();

        window.addEventListener('resize', () => this.resize());
    }

    public getPages(): Map<string, Page> {
        return this.pageManager.getPageMap();
    }

    public getActivePageId(): string | null {
        return this.pageManager.getActivePageId();
    }

    public setActivePageId(id: string | null): void {
        if (id) this.pageManager.setActivePage(id);
    }

    public render(): void {
        this.renderer.render(
            this.getPages(),
            this.scale,
            this.offset,
            this.getActivePageId()
        );
        this.postRenderCallback?.();
        this.updateZoomIndicator();
    }

    public screenToWorld(x: number, y: number): Point {
        return {
            x: (x - this.offset.x) / this.scale,
            y: (y - this.offset.y) / this.scale
        };
    }

    public worldToScreen(wx: number, wy: number): Point {
        return {
            x: wx * this.scale + this.offset.x,
            y: wy * this.scale + this.offset.y
        };
    }

    // Public API
    public addPage(width: number, height: number): string {
        const id = this.pageManager.addPage(width, height);
        this.focusPage(id);
        return id;
    }

    public removePage(id: string) {
        this.pageManager.removePage(id);
    }

    public focusPage(id: string) {
        const page = this.pageManager.get(id);
        if (!page) return;

        this.pageManager.setActivePage(id);
        this.onUpdateCallback?.(id);

        const targetX = (this.canvas.width - page.width * this.scale) / 2 - (page.x * this.scale);
        const targetY = (this.canvas.height - page.height * this.scale) / 2 - (page.y * this.scale);

        this.offset.x = targetX;
        this.offset.y = targetY;
        this.render();
    }

    public clear(pageWidth: number = 1024, pageHeight: number = 1024) {
        this.pageManager.clear();
        this.historyManager.clear();
        this.addPage(pageWidth, pageHeight);
    }

    public async undo() {
        const actions = this.historyManager.undo();
        if (actions) await this.redraw(actions);
    }

    public async redo() {
        const actions = this.historyManager.redo();
        if (actions) await this.redraw(actions);
    }

    public async redraw(actions?: DrawingAction[]) {
        const targetActions = actions || this.historyManager.getActions();
        const snapshot = this.historyManager.getLatestSnapshot(targetActions.length);
        let startIndex = 0;

        if (snapshot) {
            this.applySnapshot(snapshot.data);
            startIndex = snapshot.index + 1;
        } else {
            this.getPages().forEach(page => {
                page.ctx.fillStyle = 'white';
                page.ctx.fillRect(0, 0, page.width, page.height);
            });
        }

        const editingIndex = this.textTool?.getEditingActionIndex() ?? -1;

        for (let index = startIndex; index < targetActions.length; index++) {
            const action = targetActions[index];
            if (index === editingIndex) continue;

            if (action.pageId) {
                const p = this.pageManager.get(action.pageId);
                if (p) await action.draw(p.ctx);
            }
        }
        this.render();
        this.onUpdateCallback?.();
    }

    public pushAction(action: DrawingAction) {
        this.historyManager.push(action);
        if (this.historyManager.getCount() % 20 === 0) {
            this.historyManager.addSnapshot(this.takeSnapshot());
        }
    }

    private takeSnapshot(): Map<string, ImageData> {
        const snapshot = new Map<string, ImageData>();
        this.getPages().forEach(page => {
            snapshot.set(page.id, page.ctx.getImageData(0, 0, page.width, page.height));
        });
        return snapshot;
    }

    private applySnapshot(snapshot: Map<string, ImageData>) {
        this.getPages().forEach(page => {
            const data = snapshot.get(page.id);
            if (data) {
                page.ctx.putImageData(data, 0, 0);
            } else {
                page.ctx.fillStyle = 'white';
                page.ctx.fillRect(0, 0, page.width, page.height);
            }
        });
    }

    public setTool(tool: DrawingTool) {
        this.toolManager.setTool(tool);
    }

    public setConfig(config: Partial<ToolConfig>) {
        this.config = { ...this.config, ...config };
    }

    public resize() {
        if (!this.canvas.parentElement) return;
        const rect = this.canvas.parentElement.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
        this.offscreenCanvas.width = rect.width;
        this.offscreenCanvas.height = rect.height;

        if (this.offset.x === 0 && this.offset.y === 0 && this.pageManager.getAll().length > 0) {
            const first = this.pageManager.getAll()[0];
            this.focusPage(first.id);
        }
        this.render();
    }

    public zoomIn() {
        this.scale = Math.min(10, this.scale * 1.1);
        this.render();
    }

    public zoomOut() {
        this.scale = Math.max(0.1, this.scale / 1.1);
        this.render();
    }

    private updateZoomIndicator() {
        this.onZoomChange?.(Math.round(this.scale * 100));
    }

    public exportImage() {
        const id = this.getActivePageId();
        if (!id) {
            showToast('No active page to export.');
            return;
        }
        const page = this.pageManager.get(id)!;
        const link = document.createElement('a');
        link.download = 'idrawbook-' + id + '-' + Date.now() + '.png';
        link.href = page.canvas.toDataURL();
        link.click();
    }

    // Selection operations (delegate to SelectTool)
    private selectTool(): SelectTool {
        return this.toolManager.getTool('select') as SelectTool;
    }

    public hasSelection(): boolean {
        return this.selectTool().hasSelection();
    }

    public deleteSelection(): boolean {
        return this.selectTool().deleteSelection();
    }

    public fillSelection(): boolean {
        return this.selectTool().fillSelection();
    }

    public invertSelection(): void {
        const page = this.pageManager.getActivePage();
        if (page) this.selectTool().invertSelection(page);
    }

    public selectAll(): void {
        const page = this.pageManager.getActivePage();
        if (page) this.selectTool().selectAll(page);
    }

    public deselect(): void {
        this.selectTool().deselect();
    }

    public async copySelection(): Promise<boolean> {
        return this.selectTool().copySelection();
    }

    public async cutSelection(): Promise<boolean> {
        return this.selectTool().cutSelection();
    }

    public async pasteFromClipboard(): Promise<boolean> {
        const page = this.pageManager.getActivePage();
        if (!page) return false;

        let imageData: ImageData;
        try {
            const items = await navigator.clipboard.read();
            let blob: Blob | null = null;
            for (const item of items) {
                if (item.types.includes('image/png')) {
                    blob = await item.getType('image/png');
                    break;
                }
            }
            if (!blob) return false;
            const bitmap = await createImageBitmap(blob);
            const tmp = document.createElement('canvas');
            tmp.width = bitmap.width;
            tmp.height = bitmap.height;
            const tmpCtx = tmp.getContext('2d')!;
            tmpCtx.drawImage(bitmap, 0, 0);
            imageData = tmpCtx.getImageData(0, 0, bitmap.width, bitmap.height);
        } catch (err) {
            console.warn('Clipboard read failed:', err);
            return false;
        }

        // Center paste within the current viewport, clamped to page bounds
        const viewCX = (this.canvas.width / 2 - this.offset.x) / this.scale;
        const viewCY = (this.canvas.height / 2 - this.offset.y) / this.scale;
        const pasteX = Math.round(Math.max(0, Math.min(page.width - imageData.width, viewCX - page.x - imageData.width / 2)));
        const pasteY = Math.round(Math.max(0, Math.min(page.height - imageData.height, viewCY - page.y - imageData.height / 2)));

        const action = new PasteAction(imageData, pasteX, pasteY, this.config, page.id);
        action.draw(page.ctx);
        this.pushAction(action);
        this.render();
        this.onUpdateCallback?.();
        return true;
    }

    /**
     * Copies the selected pixels to a brand-new page sized to the bounding box.
     * Returns the new page ID, or null if there is no active selection.
     */
    public copyToNewPage(): string | null {
        const data = this.selectTool().getSelectionMask();
        if (!data) return null;

        const { mask, width: w, height: h, page } = data;

        // Compute bounding box
        let minX = w, minY = h, maxX = 0, maxY = 0;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                if (mask[y * w + x]) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
        }
        if (minX > maxX || minY > maxY) return null;

        const bw = maxX - minX + 1;
        const bh = maxY - minY + 1;

        const srcData = page.ctx.getImageData(minX, minY, bw, bh);
        const d = srcData.data;

        // Fill non-selected pixels with white
        for (let y = 0; y < bh; y++) {
            for (let x = 0; x < bw; x++) {
                if (!mask[(minY + y) * w + (minX + x)]) {
                    const p = (y * bw + x) * 4;
                    d[p] = 255; d[p + 1] = 255; d[p + 2] = 255; d[p + 3] = 255;
                }
            }
        }

        const newPageId = this.pageManager.addPage(bw, bh);
        const newPage = this.pageManager.get(newPageId);
        if (newPage) {
            newPage.ctx.putImageData(srcData, 0, 0);
        }
        this.render();
        this.onUpdateCallback?.();
        return newPageId;
    }

    // Legacy support methods
    public canUndo() { return this.historyManager.canUndo(); }
    public canRedo() { return this.historyManager.canRedo(); }
    public setHistoryLimit(limit: number) { this.historyManager.setLimit(limit); }
    public getPixelSize() {
        const page = this.pageManager.getActivePage();
        return page ? { width: page.width, height: page.height } : { width: 0, height: 0 };
    }

    // Internal

    public getThumbnail(width: number = 200, pageId?: string): string {
        let targetCanvas: HTMLCanvasElement;
        const page = pageId ? this.pageManager.get(pageId) : this.pageManager.getActivePage();

        if (page) {
            targetCanvas = page.canvas;
        } else if (this.pageManager.getAll().length > 0) {
            targetCanvas = this.pageManager.getAll()[0].canvas;
        } else {
            return '';
        }

        const ratio = targetCanvas.height / targetCanvas.width;
        const height = width * ratio;

        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = width;
        thumbCanvas.height = height;
        const ctx = thumbCanvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(targetCanvas, 0, 0, width, height);
        }
        return thumbCanvas.toDataURL('image/jpeg', 0.7);
    }

    public async getBlob(format: 'png' | 'jpeg' | 'pdf' = 'png', quality: number = 0.9): Promise<Blob | null> {
        const page = this.pageManager.getActivePage();
        if (!page) {
            return null;
        }

        if (format === 'pdf') {
            // Lazy-load jsPDF only when PDF export is needed
            // Reduces initial bundle size by ~300KB
            const { jsPDF } = await import('jspdf');

            const orientation = page.width > page.height ? 'l' : 'p';
            const pdf = new jsPDF({
                orientation,
                unit: 'px',
                format: [page.width, page.height]
            });
            const imgData = page.canvas.toDataURL('image/jpeg', quality);
            pdf.addImage(imgData, 'JPEG', 0, 0, page.width, page.height);
            return pdf.output('blob');
        } else {
            return new Promise(resolve => {
                const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
                page.canvas.toBlob(blob => {
                    resolve(blob);
                }, mimeType, quality);
            });
        }
    }

    public async loadFromBlob(blob: Blob, shouldClear: boolean = true) {
        try {
            if (blob.type === 'application/pdf') {
                await this.loadPdf(blob, shouldClear);
            } else if (blob.type.startsWith('image/')) {
                await this.loadImage(blob, shouldClear);
            } else {
                showToast('Unsupported file type: ' + blob.type);
            }
        } catch (err) {
            console.error(err);
            showToast('Failed to open file.');
        }
    }

    private async loadImage(blob: Blob, shouldClear: boolean) {
        const bitmap = await createImageBitmap(blob);

        if (shouldClear) {
            this.pageManager.clear();
            this.historyManager.clear();
        }

        const pageId = this.addPage(bitmap.width, bitmap.height);
        const page = this.pageManager.get(pageId);
        if (page) {
            page.ctx.drawImage(bitmap, 0, 0);
        }
        this.render();
    }

    private async loadPdf(blob: Blob, shouldClear: boolean) {
        // Dynamic import for pdfjs-dist
        const pdfjsLib = await import('pdfjs-dist');
        // Set worker. using standard Vite URL import for assets or node_modules resolution
        // We use the minified mjs worker
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

        const arrayBuffer = await blob.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        if (shouldClear) {
            this.pageManager.clear();
            this.historyManager.clear();
        }

        const scale = 1.5; // Better quality

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale });

            const pageId = this.addPage(viewport.width, viewport.height);
            const canvasPage = this.pageManager.get(pageId);

            if (canvasPage) {
                await page.render({
                    canvasContext: canvasPage.ctx,
                    viewport: viewport,
                    canvas: canvasPage.canvas
                }).promise;
            }
        }
        // Focus first page
        if (this.pageManager.getAll().length > 0) {
            this.focusPage(this.pageManager.getAll()[0].id);
        }
        this.render();
    }
}
